import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const WARNING_TIME = 25 * 60 * 1000; // 25 minutes
const TOKEN_KEY = "rr_auth_token";

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const inactivityTimer = useRef(null);
  const warningTimer = useRef(null);
  const warningShown = useRef(false);

  const lock = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setIsAuthenticated(false);
    clearTimeout(inactivityTimer.current);
    clearTimeout(warningTimer.current);
    warningShown.current = false;
  }, []);

  const resetInactivityTimer = useCallback(() => {
    if (!isAuthenticated) return;

    clearTimeout(inactivityTimer.current);
    clearTimeout(warningTimer.current);
    warningShown.current = false;

    warningTimer.current = setTimeout(() => {
      warningShown.current = true;
      toast.warning("Session will lock in 5 minutes due to inactivity", {
        duration: 10000,
        id: "inactivity-warning",
      });
    }, WARNING_TIME);

    inactivityTimer.current = setTimeout(() => {
      toast.info("Session locked due to inactivity");
      lock();
    }, INACTIVITY_TIMEOUT);
  }, [isAuthenticated, lock]);

  // Set up activity listeners
  useEffect(() => {
    if (!isAuthenticated) return;

    const events = ["click", "keypress", "touchstart", "scroll", "mousemove"];
    const handler = () => resetInactivityTimer();

    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    resetInactivityTimer();

    return () => {
      events.forEach((e) => window.removeEventListener(e, handler));
      clearTimeout(inactivityTimer.current);
      clearTimeout(warningTimer.current);
    };
  }, [isAuthenticated, resetInactivityTimer]);

  // Check existing session on mount
  useEffect(() => {
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (!token) {
      setIsChecking(false);
      return;
    }

    fetch(`${API}/auth/validate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.valid) {
          setIsAuthenticated(true);
        } else {
          sessionStorage.removeItem(TOKEN_KEY);
        }
      })
      .catch(() => {
        // If backend is unreachable, trust the sessionStorage token
        setIsAuthenticated(true);
      })
      .finally(() => setIsChecking(false));
  }, []);

  const login = async (passcode) => {
    const res = await fetch(`${API}/auth/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ passcode }),
    });
    const data = await res.json();

    if (data.success) {
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setIsAuthenticated(true);
      return { success: true };
    }

    return {
      success: false,
      locked: data.locked || false,
      remaining_seconds: data.remaining_seconds || 0,
      attempts_remaining: data.attempts_remaining,
      message: data.message || "Invalid passcode",
    };
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isChecking, login, lock }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
